import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { useHandleNetworkError } from "utils/useHandleNetworkError";

/**
 * Record or clear how a family was chased about one outstanding course (KIN-1890).
 *
 * State is per patient AND per course, which is why both are in the path: a parent
 * contacted about MMR has not been contacted about the 6-in-1.
 *
 * Both invalidate the whole recall query rather than patching a row. The list is derived
 * on request — snoozing a course can remove it, and a contact changes what the row says —
 * so re-reading is the only thing that gives an honest answer.
 */
const recallStateKey = (tenantVal, patientId, courseKey) =>
  `${tenantVal}/immunisationschedule/recall/${patientId}/${courseKey}`;

export const useSetRecallState = () => {
  const queryClient = useQueryClient();
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const handleNetworkError = useHandleNetworkError(
    "Could not record the contact"
  );

  return useMutation({
    mutationFn: async ({ patientId, courseKey, request }) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.put(
        recallStateKey(tenantVal, patientId, courseKey),
        request
      );

      return response.data;
    },
    onSuccess: () => {
      toast.success("Recall updated.");
      queryClient.invalidateQueries({ queryKey: ["vaccineRecall", tenantVal] });
    },
    onError: handleNetworkError,
  });
};

export const useClearRecallState = () => {
  const queryClient = useQueryClient();
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const handleNetworkError = useHandleNetworkError(
    "Could not clear the recall state"
  );

  return useMutation({
    mutationFn: async ({ patientId, courseKey }) => {
      const axiosInstance = createAxiosInstance();

      await axiosInstance.delete(
        recallStateKey(tenantVal, patientId, courseKey)
      );
    },
    onSuccess: () => {
      // "Cleared", not "removed": the backend soft-deletes so the audit trail survives,
      // and the course can be marked again afterwards.
      toast.success("Recall state cleared.");
      queryClient.invalidateQueries({ queryKey: ["vaccineRecall", tenantVal] });
    },
    onError: handleNetworkError,
  });
};
